import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { supabase } from '../lib/supabaseClient';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faBars,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflowY: 'auto',
};

const Addresses = ({ onAddressSelect, selectionType }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    name: '',
    description: '',
    city: '',
    state: '',
    country: '',
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [viewport, setViewport] = useState({
    center: {
      lat: 41.0082,
      lng: 28.9784,
    },
    zoom: 10,
  });
  const [editingAddress, setEditingAddress] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [addressesPerPage, setAddressesPerPage] = useState(10);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [addressSearchQuery, setAddressSearchQuery] = useState('');

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAddresses(data || []);
    } catch (error) {
      console.error('Error fetching addresses:', error.message);
    }
  };

  const handleAddAddress = async () => {
    try {
      if (editingAddress) {
        const { error } = await supabase
          .from('addresses')
          .update(newAddress)
          .eq('id', editingAddress.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('addresses').insert([newAddress]);
        if (error) throw error;
      }

      setOpenModal(false);
      setNewAddress({
        name: '',
        description: '',
        city: '',
        state: '',
        country: '',
      });
      setEditingAddress(null);
      fetchAddresses();
    } catch (error) {
      console.error('Error saving address:', error.message);
    }
  };

  const handleDeleteAddress = async (id) => {
    try {
      const { error } = await supabase.from('addresses').delete().eq('id', id);
      if (error) throw error;
      fetchAddresses();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting address:', error.message);
    }
  };

  const openDeleteDialog = (id) => {
    setAddressToDelete(id);
    setShowDeleteDialog(true);
  };

  const handleLocationSelect = (prediction) => {
    // Check if Google Maps API is loaded
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps API not loaded yet');
      return;
    }

    try {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      service.getDetails({ placeId: prediction.place_id }, (place, status) => {
        if (status === 'OK' && place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          setSelectedLocation({ lat, lng });
          setViewport({
            center: { lat, lng },
            zoom: 14,
          });

          setNewAddress((prev) => ({
            ...prev,
            description: place.formatted_address,
            city:
              place.address_components.find((c) => c.types.includes('administrative_area_level_1'))
                ?.long_name || '',
            country:
              place.address_components
                .find((c) => c.types.includes('country'))
                ?.short_code?.toUpperCase() || '',
            latitude: lat.toString(),
            longitude: lng.toString(),
          }));
          setSearchResults([]);
          setSearchQuery(place.formatted_address);
        }
      });
    } catch (error) {
      console.error('Error in Google Maps place details:', error);
    }
  };

  useEffect(() => {
    const searchLocation = async () => {
      if (!searchQuery) {
        setSearchResults([]);
        return;
      }

      // Check if Google Maps API is loaded
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('Google Maps API not loaded yet');
        return;
      }

      try {
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          { input: searchQuery },
          (predictions, status) => {
            if (status === 'OK') {
              setSearchResults(predictions);
            } else {
              setSearchResults([]);
            }
          }
        );
      } catch (error) {
        console.error('Error in Google Maps autocomplete:', error);
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(searchLocation, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleEditClick = (address) => {
    setEditingAddress(address);
    setNewAddress({
      name: address.name,
      description: address.description,
      city: address.city,
      state: address.state,
      country: address.country,
      latitude: address.latitude,
      longitude: address.longitude,
    });
    // Set the searchQuery to the address description for the "Açık Adres" field
    setSearchQuery(address.description || '');
    setOpenModal(true);
    if (address.latitude && address.longitude) {
      setSelectedLocation({
        lng: parseFloat(address.longitude),
        lat: parseFloat(address.latitude),
      });
      setViewport({
        center: {
          lat: parseFloat(address.latitude),
          lng: parseFloat(address.longitude),
        },
        zoom: 14,
      });
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setEditingAddress(null);
    setNewAddress({
      name: '',
      description: '',
      city: '',
      state: '',
      country: '',
    });
    // Reset the searchQuery when closing the modal
    setSearchQuery('');
    setSelectedLocation(null);
  };

  const filteredAddresses = addresses.filter((address) => {
    const searchLower = addressSearchQuery.toLowerCase();
    return (
      address.name?.toLowerCase().includes(searchLower) ||
      address.description?.toLowerCase().includes(searchLower) ||
      address.city?.toLowerCase().includes(searchLower) ||
      address.state?.toLowerCase().includes(searchLower) ||
      address.country?.toLowerCase().includes(searchLower)
    );
  });

  const indexOfLastAddress = currentPage * addressesPerPage;
  const indexOfFirstAddress = indexOfLastAddress - addressesPerPage;
  const currentAddresses = filteredAddresses.slice(
    indexOfFirstAddress,
    indexOfLastAddress
  );
  const totalPages = Math.ceil(filteredAddresses.length / addressesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle change in number of addresses per page
  const handleAddressesPerPageChange = (e) => {
    setAddressesPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters or addressesPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [addressSearchQuery, addressesPerPage]);

  const handleAddressSelect = (address) => {
    if (onAddressSelect) {
      onAddressSelect(address);
    }
  };

  // Function to find address from coordinates
  const findAddressFromCoordinates = (lat, lng) => {
    if (!isNaN(lat) && !isNaN(lng)) {
      // Set selected location and make map visible
      setSelectedLocation({ lat, lng });
      setIsMapVisible(true);

      // Update viewport to center on this location
      setViewport({
        center: { lat, lng },
        zoom: 14,
      });

      // Reverse geocode to get the address
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const addressComponents = results[0].address_components;

            // Update the address fields
            setNewAddress((prev) => ({
              ...prev,
              description: results[0].formatted_address,
              city:
                addressComponents.find((c) => c.types.includes('administrative_area_level_1'))
                  ?.long_name || '',
              state:
                addressComponents.find((c) =>
                  c.types.includes('administrative_area_level_1')
                )?.long_name || '',
              country:
                addressComponents.find((c) => c.types.includes('country'))
                  ?.short_name || '',
              latitude: lat.toString(),
              longitude: lng.toString(),
            }));
            setSearchQuery(results[0].formatted_address);
            alert('Adres başarıyla bulundu!');
          } else {
            alert('Koordinatlara ait adres bulunamadı.');
          }
        });
      }
    } else {
      alert('Lütfen geçerli koordinat değerleri girin.');
    }
  };

  // Function to geocode an address
  const geocodeAddress = (address) => {
    if (!address || !window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        // Set the selected location for the map marker
        setSelectedLocation({ lat, lng });

        // Update the viewport to center on this location
        setViewport({
          center: { lat, lng },
          zoom: 14,
        });

        // Update the address data with coordinates and components
        const addressComponents = results[0].address_components;
        setNewAddress((prev) => ({
          ...prev,
          description: results[0].formatted_address,
          city:
            addressComponents.find((c) => c.types.includes('administrative_area_level_1'))
              ?.long_name || '',
          state:
            addressComponents.find((c) =>
              c.types.includes('administrative_area_level_1')
            )?.long_name || '',
          country:
            addressComponents.find((c) => c.types.includes('country'))
              ?.short_name || '',
          latitude: lat.toString(),
          longitude: lng.toString(),
        }));

        // Update the searchQuery with the formatted address
        setSearchQuery(results[0].formatted_address);

        // Show the map to display the found location
        setIsMapVisible(true);
      } else {
        // Show error if geocoding failed
        alert(
          'Adres için konum bulunamadı. Lütfen daha spesifik bir adres girin.'
        );
      }
    });
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-medium text-gray-800">Adresler</h1>
              <button
                onClick={() => setOpenModal(true)}
                className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
              >
                <AddIcon className="mr-2" />
                Yeni Adres Ekle
              </button>
            </div>

            {/* Add search box */}
            <div className="w-full">
              <TextField
                fullWidth
                placeholder="Adreslerde ara..."
                variant="outlined"
                size="small"
                value={addressSearchQuery}
                onChange={(e) => {
                  setAddressSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ color: 'action.active', mr: 1, my: 0.5 }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </Box>
                  ),
                }}
              />
            </div>
          </div>

          {/* Users List */}
          <div className="bg-white rounded-lg shadow mt-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Adres Adı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Adres
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    İl
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    İlçe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Ülke
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Eylemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentAddresses.map((address) => (
                  <tr
                    key={address.id}
                    onClick={() => handleAddressSelect(address)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {address.name || 'İsimsiz Adres'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {address.description}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-500">{address.city}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-500">{address.state}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-500">{address.country}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(address);
                        }}
                        sx={{
                          color: '#4263EB',
                          '&:hover': {
                            color: '#3b5bd9',
                          },
                        }}
                        title="Düzenle"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(address.id);
                        }}
                        sx={{
                          color: '#dc2626',
                          '&:hover': {
                            color: '#b91c1c',
                          },
                        }}
                        title="Sil"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
                {addresses.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      Henüz adres eklenmemiş
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
                      ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    Previous
                  </button>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">
                      Sayfa başına:
                    </span>
                    <select
                      value={addressesPerPage}
                      onChange={handleAddressesPerPageChange}
                      className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
                      ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700 mr-4">
                    Showing{' '}
                    <span className="font-medium">
                      {indexOfFirstAddress + 1}
                    </span>{' '}
                    -{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastAddress, filteredAddresses.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">
                      {filteredAddresses.length}
                    </span>{' '}
                    results
                  </p>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">
                      Sayfa başına:
                    </span>
                    <select
                      value={addressesPerPage}
                      onChange={handleAddressesPerPageChange}
                      className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium
                        ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="h-4 w-4"
                      />
                    </button>

                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                              ${
                                currentPage === pageNumber
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                      if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}

                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium
                        ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="h-4 w-4"
                      />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* New Address Modal */}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[600px] max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingAddress ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Adres Adı
                </label>
                <input
                  type="text"
                  value={newAddress.name}
                  onChange={(e) =>
                    setNewAddress({
                      ...newAddress,
                      name: e.target.value,
                    })
                  }
                  className="w-full border-2 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Açık Adres
                </label>
                <textarea
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border-2 rounded-md p-2"
                  rows="3"
                ></textarea>
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => {
                    if (searchQuery) {
                      geocodeAddress(searchQuery);
                    } else {
                      alert('Lütfen bir adres girin.');
                    }
                  }}
                  className="w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Konumu Bul
                </button>
              </div>

              {/* Map Toggle Button */}
              <button
                type="button"
                onClick={() => setIsMapVisible(!isMapVisible)}
                className="w-full px-4 py-2 text-left text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md flex items-center justify-between"
              >
                <span>Haritada Göster</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${
                    isMapVisible ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Map Section */}
              {isMapVisible && (
                <div className="relative h-[300px]">
                  <APIProvider apiKey={'AIzaSyDB8KdSyduBU2vadQbJhojnwu5gNsj2f-o'} libraries={['places']}>
                    <Map
                      zoom={viewport.zoom}
                      center={viewport.center}
                      gestureHandling="greedy"
                      disableDefaultUI={false}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '0.375rem',
                      }}
                      onCameraChanged={(ev) => {
                        setViewport({
                          center: ev.detail.center,
                          zoom: ev.detail.zoom,
                        });
                      }}
                      
                      onClick={(event) => {
                        if (!event.detail.latLng) return; 

                        const lat = event.detail.latLng.lat;
                        const lng = event.detail.latLng.lng;
                        setSelectedLocation({ lat, lng });

                        setViewport((prev) => ({
                          ...prev,
                          center: { lat, lng },
                        }));

                        const geocoder = new window.google.maps.Geocoder();
                        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                          if (status === 'OK' && results[0]) {
                            const addressComponents = results[0].address_components;
                            setNewAddress((prev) => ({
                              ...prev,
                              description: results[0].formatted_address,
                              city:
                                addressComponents.find((c) =>
                                  c.types.includes('administrative_area_level_1')
                                )?.long_name || '',
                              country:
                                addressComponents.find((c) => c.types.includes('country'))
                                  ?.short_name || '',
                              latitude: lat.toString(),
                              longitude: lng.toString(),
                            }));
                            setSearchQuery(results[0].formatted_address);
                          }
                        });
                      }}
                    >
                      {selectedLocation && (
                        <Marker
                          position={{
                            lat: selectedLocation.lat,
                            lng: selectedLocation.lng,
                          }}
                        />
                      )}
                    </Map>
                  </APIProvider>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Şehir
                  </label>
                  <input
                    type="text"
                    value={newAddress.city}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, city: e.target.value })
                    }
                    className="w-full border-2 rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ülke</label>
                  <input
                    type="text"
                    value={newAddress.country}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, country: e.target.value })
                    }
                    className="w-full border-2 rounded-md p-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    X Koordinat
                  </label>
                  <input
                    type="text"
                    value={newAddress.latitude || ''}
                    onChange={(e) =>
                      setNewAddress({ ...newAddress, latitude: e.target.value })
                    }
                    className="w-full border-2 rounded-md p-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Y Koordinat
                  </label>
                  <input
                    type="text"
                    value={newAddress.longitude || ''}
                    onChange={(e) =>
                      setNewAddress({
                        ...newAddress,
                        longitude: e.target.value,
                      })
                    }
                    className="w-full border-2 rounded-md p-2"
                  />
                </div>
              </div>

              {/* Add button to find address from coordinates */}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => {
                    // Check if both latitude and longitude are provided
                    if (newAddress.latitude && newAddress.longitude) {
                      const lat = parseFloat(newAddress.latitude);
                      const lng = parseFloat(newAddress.longitude);
                      findAddressFromCoordinates(lat, lng);
                    } else {
                      alert(
                        'Lütfen hem enlem hem de boylam değerlerini girin.'
                      );
                    }
                  }}
                  className="w-full py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Koordinatlardan Adres Bul
                </button>
              </div>

              {/* Add button to find location from address */}
            </div>

            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={handleModalClose}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleAddAddress}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingAddress ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" mb={2}>
            Adresi Sil
          </Typography>
          <Typography variant="body1" mb={3}>
            Bu adresi silmek istediğinizden emin misiniz? Bu işlem geri
            alınamaz.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setShowDeleteDialog(false)}
            >
              İptal Et
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeleteAddress(addressToDelete)}
            >
              Sil
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default Addresses;
